// Espace Secrétariat : d'après la maquette de référence (bleu marine, bleu vif, jaune doré).
// Toutes les données viennent de l'API ; les droits sont vérifiés par le serveur.
import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import { qlConfirm } from "../../utils/dialogs";
import DocumentViewer from "../DocumentViewer";
import { Chargement, Chip, Entete, Fenetre, Kpi, Message } from "../compta/ComptaEspace";
import { MULTIPART, TON_DEMANDE, dateHeure, erreurApi, formulaire, ouvrirPage, versChampDateHeure } from "../community/cmOutils";
import "../compta/ComptaEspace.css";
import "./SecretariatEspace.css";

const TONS = { navy: "#09245b", blue: "#2563eb", gold: "#c58f00", green: "#15803d" };
const TON_STATUT = {
  a_faire: "amber", en_cours: "", terminee: "green",
  a_confirmer: "amber", confirme: "green", termine: "grey", annule: "grey",
  prospect: "violet", en_discussion: "", a_relancer: "amber", partenaire: "green", sans_suite: "grey",
  nouvelle: "violet", en_preparation: "", livree: "green", annulee: "grey",
  a_traiter: "amber", transmis: "", traite: "green", archive: "grey",
  brouillon: "grey", a_valider: "amber", valide: "green", emis: "",
};
const TON_PRIORITE = { haute: "red", normale: "", basse: "grey" };

// Champs du formulaire et libellés propres à chaque registre.
export const REGISTRES = {
  tache: {
    titre: "Mes tâches", sousTitre: "Suivez vos priorités et les tâches confiées par la direction.", nouveau: "Nouvelle tâche",
    champs: ["titre", "categorie", "priorite", "echeance", "assignee", "details"],
    libelles: { titre: "Tâche", echeance: "Échéance" }, priorite: true,
  },
  rendez_vous: {
    titre: "Agenda", sousTitre: "Organisez les rendez-vous et réunions de Quick Learning.", nouveau: "Nouveau rendez-vous",
    champs: ["titre", "echeance", "categorie", "contact_nom", "contact", "ville", "assignee", "details"],
    libelles: { titre: "Objet du rendez-vous", echeance: "Date et heure", categorie: "Service concerné", contact_nom: "Avec", ville: "Lieu" },
  },
  etablissement: {
    titre: "Établissements", sousTitre: "Suivez les prospects, les partenaires et les relances.", nouveau: "Nouvel établissement",
    champs: ["titre", "ville", "contact_nom", "contact", "echeance", "priorite", "assignee", "details"],
    libelles: { titre: "Nom de l’établissement", contact_nom: "Interlocuteur", contact: "Contact professionnel", echeance: "Prochaine relance" },
    priorite: true,
  },
  commande: {
    titre: "Commandes de manuels", sousTitre: "Suivi administratif des demandes et commandes, sans aucun paiement.", nouveau: "Nouvelle commande",
    champs: ["titre", "manuel", "quantite", "contact_nom", "contact", "ville", "echeance", "details"],
    libelles: { titre: "Demandeur (famille, établissement…)", contact_nom: "Personne à contacter", echeance: "Livraison souhaitée" },
  },
  courrier: {
    titre: "Courriers", sousTitre: "Enregistrez, classez et transmettez les courriers entrants et sortants.", nouveau: "Enregistrer un courrier",
    champs: ["sens", "titre", "reference_externe", "contact_nom", "categorie", "echeance", "assignee", "piece_jointe", "details"],
    libelles: { titre: "Objet", reference_externe: "Référence du courrier", contact_nom: "Expéditeur ou destinataire", echeance: "Date de réponse attendue" },
  },
  document: {
    titre: "Documents", sousTitre: "Préparez des documents soumis à la validation du CEO avant leur émission.", nouveau: "Nouveau document",
    champs: ["titre", "categorie", "details", "piece_jointe"],
    libelles: { titre: "Titre du document", categorie: "Type (convocation, compte rendu, attestation…)", details: "Contenu" },
  },
};

const LIBELLES_CHAMPS = {
  titre: "Objet", categorie: "Catégorie", priorite: "Priorité", echeance: "Échéance", assignee: "Confier à",
  contact_nom: "Nom du contact", contact: "E-mail ou téléphone professionnel", ville: "Ville", sens: "Sens",
  reference_externe: "Référence", manuel: "Manuel", quantite: "Nombre d’exemplaires", details: "Notes", piece_jointe: "Pièce jointe",
};

function useEquipe() {
  const [equipe, setEquipe] = useState([]);
  useEffect(() => { api.get("secretariat/equipe/").then((r) => setEquipe(r.data.equipe || [])).catch(() => {}); }, []);
  return equipe;
}

function useManuels() {
  const [manuels, setManuels] = useState(null);
  const charger = useCallback(() => {
    api.get("secretariat/manuels/").then((r) => setManuels(r.data.manuels || [])).catch(() => setManuels([]));
  }, []);
  useEffect(charger, [charger]);
  return [manuels, charger];
}

function Pagination({ pagination, onPage }) {
  if (!pagination || pagination.pages <= 1) return null;
  return (
    <div className="qs-pagination" role="navigation" aria-label="Pagination">
      <button className="qc-btn qc-btn--sm" disabled={pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}>← Précédent</button>
      <span>Page {pagination.page} sur {pagination.pages} · {pagination.total} résultats</span>
      <button className="qc-btn qc-btn--sm" disabled={pagination.page >= pagination.pages} onClick={() => onPage(pagination.page + 1)}>Suivant →</button>
    </div>
  );
}

// ── Formulaire et fiche d'un dossier ────────────────────────────────────────

function FormDossier({ type, dossier, statuts, onClose, onSaved }) {
  const cfg = REGISTRES[type];
  const equipe = useEquipe();
  const [manuels] = useManuels();
  const [valeurs, setValeurs] = useState(() => ({
    titre: dossier?.titre || "", categorie: dossier?.categorie || "", priorite: dossier?.priorite || "normale",
    echeance: versChampDateHeure(dossier?.echeance), assignee: dossier?.assignee?.id || "",
    contact_nom: dossier?.contact_nom || "", contact: dossier?.contact || "", ville: dossier?.ville || "",
    sens: dossier?.sens || "entrant", reference_externe: dossier?.reference_externe || "",
    manuel: dossier?.manuel?.id || "", quantite: dossier?.quantite || "", details: dossier?.details || "",
    statut: dossier?.statut || "",
  }));
  const [fichier, setFichier] = useState(null);
  const [message, setMessage] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const maj = (cle) => (e) => setValeurs((v) => ({ ...v, [cle]: e.target.value }));
  const libelle = (cle) => cfg.libelles?.[cle] || LIBELLES_CHAMPS[cle];

  const enregistrer = async (e) => {
    e.preventDefault();
    setEnvoi(true); setMessage("");
    const donnees = formulaire({ ...valeurs, type, statut: type === "document" || !dossier ? undefined : valeurs.statut });
    if (fichier) donnees.append("piece_jointe", fichier);
    try {
      const r = dossier ? await api.put(`secretariat/dossiers/${dossier.id}/`, donnees, MULTIPART)
        : await api.post("secretariat/dossiers/", donnees, MULTIPART);
      onSaved(r.data);
    } catch (err) { setMessage(erreurApi(err)); } finally { setEnvoi(false); }
  };

  const champ = (cle) => {
    switch (cle) {
      case "priorite":
        return <label key={cle}>{libelle(cle)}<select value={valeurs.priorite} onChange={maj(cle)}>
          <option value="haute">Haute</option><option value="normale">Normale</option><option value="basse">Basse</option></select></label>;
      case "echeance":
        return <label key={cle}>{libelle(cle)}<input type="datetime-local" value={valeurs.echeance} onChange={maj(cle)} required={type === "rendez_vous"} /></label>;
      case "assignee":
        return <label key={cle}>{libelle(cle)}<select value={valeurs.assignee} onChange={maj(cle)}>
          <option value="">Personne</option>
          {equipe.map((m) => <option key={m.id} value={m.id}>{m.nom}{m.moi ? " (moi)" : ""}</option>)}</select></label>;
      case "sens":
        return <label key={cle}>{libelle(cle)}<select value={valeurs.sens} onChange={maj(cle)}>
          <option value="entrant">Courrier entrant</option><option value="sortant">Courrier sortant</option></select></label>;
      case "manuel":
        return <label key={cle}>{libelle(cle)}<select value={valeurs.manuel} onChange={maj(cle)}>
          <option value="">À préciser</option>
          {(manuels || []).map((m) => <option key={m.id} value={m.id}>{m.titre} · {m.niveau}</option>)}</select></label>;
      case "quantite":
        return <label key={cle}>{libelle(cle)}<input type="number" min="1" value={valeurs.quantite} onChange={maj(cle)} /></label>;
      case "details":
        return <label key={cle} className="qc-full">{libelle(cle)}<textarea rows={type === "document" ? 8 : 4} value={valeurs.details} onChange={maj(cle)}
          placeholder="Informations administratives non sensibles" /></label>;
      case "piece_jointe":
        return <label key={cle}>{libelle(cle)}{dossier?.piece_jointe_url ? " (remplace l’actuelle)" : ""}
          <input type="file" accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.odt" onChange={(e) => setFichier(e.target.files?.[0] || null)} /></label>;
      default:
        return <label key={cle} className={cle === "titre" ? "qc-full" : undefined}>{libelle(cle)}
          <input value={valeurs[cle]} onChange={maj(cle)} required={cle === "titre"} maxLength={cle === "reference_externe" ? 100 : 200} /></label>;
    }
  };

  return (
    <Fenetre titre={dossier ? `Modifier ${dossier.reference}` : cfg.nouveau} onClose={onClose}>
      <form onSubmit={enregistrer}>
        <div className="qc-form">
          {cfg.champs.map(champ)}
          {dossier && type !== "document" && (
            <label>État<select value={valeurs.statut} onChange={maj("statut")}>
              {statuts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          )}
        </div>
        {type === "document" && dossier?.statut === "valide" && (
          <div className="qs-notice">Ce document est validé : l’enregistrer à nouveau le ramène au brouillon, à faire valider.</div>
        )}
        <Message texte={message} />
        <div className="qc-actions">
          <button type="button" className="qc-btn" onClick={onClose}>Annuler</button>
          <button type="submit" className="qc-btn qc-btn--primary" disabled={envoi}>{envoi ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </form>
    </Fenetre>
  );
}

function FicheDossier({ id, statuts, onClose, onChange }) {
  const [dossier, setDossier] = useState(null);
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [edition, setEdition] = useState(false);
  const [piece, setPiece] = useState(false);
  const charger = useCallback(() => {
    api.get(`secretariat/dossiers/${id}/`).then((r) => setDossier(r.data)).catch(() => setDossier(false));
  }, [id]);
  useEffect(charger, [charger]);

  const action = async (nom, texte = "") => {
    setMessage("");
    try {
      const r = await api.post(`secretariat/dossiers/${id}/action/`, { action: nom, texte });
      setDossier(r.data); setNote(""); onChange();
    } catch (err) { setMessage(erreurApi(err)); }
  };
  const supprimer = () => qlConfirm(`Supprimer ${dossier.reference} ?`, async () => {
    try { await api.delete(`secretariat/dossiers/${id}/`); onChange(); onClose(); } catch (err) { setMessage(erreurApi(err)); }
  }, { danger: true, okLabel: "Supprimer" });

  if (edition) {
    return <FormDossier type={dossier.type} dossier={dossier} statuts={statuts} onClose={() => setEdition(false)}
      onSaved={(d) => { setDossier(d); setEdition(false); onChange(); }} />;
  }
  return (
    <Fenetre titre={dossier ? `${dossier.type_label} ${dossier.reference}` : "Dossier"} onClose={onClose}>
      {dossier === null ? <Chargement /> : dossier === false ? <div className="qc-empty">Dossier introuvable.</div> : <>
        <h4 className="qs-fiche-titre">{dossier.titre}</h4>
        <div className="qs-chips">
          <Chip ton={TON_STATUT[dossier.statut]}>{dossier.statut_label}</Chip>
          {dossier.priorite !== "normale" && <Chip ton={TON_PRIORITE[dossier.priorite]}>Priorité {dossier.priorite_label.toLowerCase()}</Chip>}
          {dossier.sens && <Chip>{dossier.sens_label}</Chip>}
        </div>
        <dl className="qs-details">
          {dossier.categorie && <><dt>Catégorie</dt><dd>{dossier.categorie}</dd></>}
          {dossier.echeance && <><dt>Date</dt><dd>{dateHeure(dossier.echeance)}</dd></>}
          {dossier.contact_nom && <><dt>Contact</dt><dd>{dossier.contact_nom}{dossier.contact ? ` · ${dossier.contact}` : ""}</dd></>}
          {dossier.ville && <><dt>Ville / lieu</dt><dd>{dossier.ville}</dd></>}
          {dossier.reference_externe && <><dt>Référence</dt><dd>{dossier.reference_externe}</dd></>}
          {dossier.manuel && <><dt>Manuel</dt><dd>{dossier.manuel.titre}{dossier.quantite ? ` × ${dossier.quantite}` : ""}</dd></>}
          {dossier.assignee && <><dt>Confié à</dt><dd>{dossier.assignee.nom}</dd></>}
          <dt>Créé par</dt><dd>{dossier.cree_par || "—"}, {dateHeure(dossier.created_at)}</dd>
          {dossier.valide_par && <><dt>Validé par</dt><dd>{dossier.valide_par}, {dateHeure(dossier.valide_le)}</dd></>}
        </dl>
        {dossier.details && <div className="qs-texte">{dossier.details}</div>}
        {dossier.piece_jointe_url && (
          <button type="button" className="qc-link" onClick={() => setPiece(true)}><i className="bi bi-paperclip me-1"></i>Ouvrir la pièce jointe</button>
        )}
        {piece && <DocumentViewer titre={dossier.titre} url={dossier.piece_jointe_url} onClose={() => setPiece(false)} />}

        {dossier.type === "document" && (
          <div className="qs-etapes" aria-label="Étapes du document">
            {["brouillon", "a_valider", "valide", "emis", "archive"].map((s) => (
              <span key={s} className={`qs-etape${dossier.statut === s ? " qs-etape--active" : ""}`}>
                {{ brouillon: "Brouillon", a_valider: "À valider", valide: "Validé", emis: "Émis", archive: "Archivé" }[s]}
              </span>
            ))}
          </div>
        )}

        <div className="qc-section-title">Historique</div>
        <ul className="qc-histo">
          {dossier.historique.map((h, i) => (
            <li key={i}><strong>{h.contenu}</strong> <span>— {h.auteur}, {dateHeure(h.date)}</span></li>
          ))}
        </ul>
        <label className="qs-note">Note de suivi ou commentaire
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note interne (visible par l’équipe du secrétariat)" />
        </label>
        <Message texte={message} />
        <div className="qc-actions qs-actions">
          {dossier.peut_supprimer && <button className="qc-btn qc-btn--danger" onClick={supprimer}>Supprimer</button>}
          {dossier.peut_modifier && <button className="qc-btn" onClick={() => setEdition(true)}>Modifier</button>}
          <button className="qc-btn" disabled={!note.trim()} onClick={() => action("note", note)}>Enregistrer la note</button>
          {dossier.type === "document" && dossier.statut === "brouillon" && (
            <button className="qc-btn qc-btn--primary" onClick={() => action("soumettre", note)}>Envoyer à la validation</button>
          )}
          {dossier.peut_valider && <>
            <button className="qc-btn" disabled={!note.trim()} title="Écrivez d’abord ce qu’il faut corriger" onClick={() => action("corriger", note)}>Demander une correction</button>
            <button className="qc-btn qc-btn--success" onClick={() => action("valider", note)}>Valider</button>
          </>}
          {dossier.type === "document" && dossier.statut === "valide" && (
            <button className="qc-btn qc-btn--primary" onClick={() => action("emettre", note)}>Marquer comme émis</button>
          )}
          {dossier.type === "document" && dossier.statut === "emis" && (
            <button className="qc-btn" onClick={() => action("archiver")}>Archiver</button>
          )}
        </div>
      </>}
    </Fenetre>
  );
}

// ── Registres (tâches, agenda, établissements, commandes, courriers, documents) ──

function infos(d) {
  switch (d.type) {
    case "tache": return [d.priorite_label, d.assignee?.nom].filter(Boolean).join(" · ");
    case "rendez_vous": return [d.categorie, d.contact_nom, d.ville].filter(Boolean).join(" · ");
    case "etablissement": return [d.ville, d.contact_nom].filter(Boolean).join(" · ");
    case "commande": return d.manuel ? `${d.manuel.titre}${d.quantite ? ` × ${d.quantite}` : ""}` : "Manuel à préciser";
    case "courrier": return [d.sens_label, d.reference_externe, d.contact_nom].filter(Boolean).join(" · ");
    default: return [d.categorie, d.cree_par].filter(Boolean).join(" · ");
  }
}

export function SECRegistrePage({ type, integre = false, statutInitial = null }) {
  const cfg = REGISTRES[type];
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState("");
  const [recherche, setRecherche] = useState("");
  const [statut, setStatut] = useState(statutInitial ?? (type === "document" || type === "courrier" ? "" : "ouverts"));
  const [priorite, setPriorite] = useState("");
  const [page, setPage] = useState(1);
  const [fiche, setFiche] = useState(null);
  const [nouveau, setNouveau] = useState(false);

  const charger = useCallback(() => {
    const params = new URLSearchParams({ type, page: String(page) });
    if (statut) params.set("statut", statut);
    if (priorite) params.set("priorite", priorite);
    if (recherche.trim()) params.set("q", recherche.trim());
    setErreur("");
    api.get(`secretariat/dossiers/?${params}`).then((r) => setDonnees(r.data))
      .catch((err) => { setErreur(erreurApi(err, "Chargement impossible.")); setDonnees({ resultats: [], statuts: [], compteurs: {} }); });
  }, [type, page, statut, priorite, recherche]);
  useEffect(() => { const t = setTimeout(charger, 250); return () => clearTimeout(t); }, [charger]);
  useEffect(() => { setPage(1); }, [statut, priorite, recherche]);

  const statuts = donnees?.statuts || [];
  const contenu = (
    <div className={integre ? "" : "qc-card"}>
      <div className="qs-card-head">
        <h3>{integre ? cfg.titre : `Registre · ${cfg.titre.toLowerCase()}`}</h3>
        {integre && <button className="qc-btn qc-btn--gold" onClick={() => setNouveau(true)}>＋ {cfg.nouveau}</button>}
      </div>
      <div className="qc-toolbar">
        <input className="qc-search qs-grow" placeholder="Rechercher dans cette liste…" value={recherche} onChange={(e) => setRecherche(e.target.value)}
          aria-label="Rechercher" />
        <select value={statut} onChange={(e) => setStatut(e.target.value)} aria-label="État">
          <option value="ouverts">En cours (non clos)</option>
          <option value="">Tous les états</option>
          {statuts.map(([k, l]) => <option key={k} value={k}>{l}{donnees?.compteurs?.[k] ? ` (${donnees.compteurs[k]})` : ""}</option>)}
        </select>
        {cfg.priorite && (
          <select value={priorite} onChange={(e) => setPriorite(e.target.value)} aria-label="Priorité">
            <option value="">Toutes priorités</option><option value="haute">Haute</option><option value="normale">Normale</option><option value="basse">Basse</option>
          </select>
        )}
      </div>
      {erreur && <Message texte={erreur} />}
      {!donnees ? <Chargement /> : (
        <div className="qc-table-wrap">
          <table className="qc-table">
            <thead><tr><th>Référence</th><th>Dossier</th><th>Informations</th><th>État</th><th>{cfg.libelles?.echeance || "Date"}</th><th>Action</th></tr></thead>
            <tbody>
              {donnees.resultats.map((d) => (
                <tr key={d.id}>
                  <td><strong>{d.reference}</strong></td>
                  <td>{d.titre}{d.priorite === "haute" && <> <Chip ton="red">Haute</Chip></>}</td>
                  <td>{infos(d) || "—"}</td>
                  <td><Chip ton={TON_STATUT[d.statut]}>{d.statut_label}</Chip></td>
                  <td>{d.echeance ? dateHeure(d.echeance) : <small>{dateHeure(d.created_at)}</small>}</td>
                  <td><button className="qc-link" onClick={() => setFiche(d.id)}>Voir</button></td>
                </tr>
              ))}
              {!donnees.resultats.length && (
                <tr><td colSpan={6} className="qc-empty">
                  {recherche || statut || priorite ? "Aucun dossier ne correspond à ces filtres." : "Aucun dossier pour le moment."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pagination pagination={donnees?.pagination} onPage={setPage} />
      {fiche && <FicheDossier id={fiche} statuts={statuts} onClose={() => setFiche(null)} onChange={charger} />}
      {nouveau && <FormDossier type={type} statuts={statuts} onClose={() => setNouveau(false)}
        onSaved={(d) => { setNouveau(false); charger(); setFiche(d.id); }} />}
    </div>
  );
  if (integre) return contenu;
  return (
    <div className="qc qs">
      <Entete titre={cfg.titre} sousTitre={cfg.sousTitre}>
        <button className="qc-btn qc-btn--gold" onClick={() => setNouveau(true)}>＋ {cfg.nouveau}</button>
      </Entete>
      {type === "document" && (
        <div className="qs-notice">Un document passe par brouillon → validation du CEO → émission → archivage. Aucune signature n’est apposée
          automatiquement : les communiqués et factures signés se rédigent dans la <button className="qc-link" onClick={() => ouvrirPage("boite_signature")}>Boîte de dépôt</button>.</div>
      )}
      {contenu}
    </div>
  );
}

// ── Tableau de bord ─────────────────────────────────────────────────────────

export function SECDashboardPage({ user, nonLus = 0 }) {
  const [donnees, setDonnees] = useState(null);
  useEffect(() => { api.get("secretariat/tableau/").then((r) => setDonnees(r.data)).catch(() => setDonnees(false)); }, []);
  const prenom = (user?.nom_complet || "").split(" ")[0];
  if (donnees === false) return <div className="qc qs"><div className="qc-empty">Le tableau de bord n’a pas pu être chargé.</div></div>;
  const c = donnees?.compteurs || {};
  return (
    <div className="qc qs">
      <Entete titre={`Bonjour${prenom ? ` ${prenom}` : ""} 👋`} sousTitre="Voici les activités administratives de Quick Learning pour aujourd’hui.">
        <button className="qc-btn qc-btn--gold" onClick={() => ouvrirPage("sec_demandes")}>Voir les demandes</button>
      </Entete>
      {!donnees ? <Chargement /> : <>
        <div className="qc-kpis">
          <Kpi label="Demandes à examiner" value={c.demandes_nouvelles} ton={TONS.navy} sub={`${c.demandes_en_cours} en cours de traitement`} />
          <Kpi label="Messages non lus" value={nonLus} ton={TONS.blue} sub="Messagerie de l’équipe" />
          <Kpi label="Rendez-vous à venir" value={c.rendez_vous} ton={TONS.gold} sub="Agenda du secrétariat" />
          <Kpi label="Tâches à effectuer" value={c.taches} ton={TONS.green} sub="Non terminées" />
        </div>
        <div className="qc-cols">
          <div>
            <div className="qc-card">
              <div className="qs-card-head"><h3>Demandes récentes</h3>
                <button className="qc-btn qc-btn--sm" onClick={() => ouvrirPage("sec_demandes")}>Tout afficher →</button></div>
              <div className="qc-table-wrap">
                <table className="qc-table">
                  <thead><tr><th>Référence</th><th>Objet</th><th>État</th></tr></thead>
                  <tbody>
                    {donnees.demandes.map((d) => (
                      <tr key={d.id}><td>{d.reference}<small>{dateHeure(d.created_at)}</small></td><td>{d.sujet}</td>
                        <td><Chip ton={TON_DEMANDE[d.statut]}>{d.statut_label}</Chip></td></tr>
                    ))}
                    {!donnees.demandes.length && <tr><td colSpan={3} className="qc-empty">Aucune demande en attente.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="qc-card" style={{ marginTop: 16 }}>
              <h3>Accès rapide</h3>
              <div className="qs-quick">
                <button onClick={() => ouvrirPage("messages")}><i className="bi bi-chat-dots"></i>Répondre aux messages →</button>
                <button onClick={() => ouvrirPage("sec_agenda")}><i className="bi bi-calendar-event"></i>Programmer un rendez-vous →</button>
                <button onClick={() => ouvrirPage("sec_courriers")}><i className="bi bi-envelope"></i>Enregistrer un courrier →</button>
                <button onClick={() => ouvrirPage("boite_signature")}><i className="bi bi-file-earmark-text"></i>Rédiger un communiqué ou une facture →</button>
              </div>
            </div>
          </div>
          <div>
            <div className="qc-card">
              <h3>📌 Priorités du jour</h3>
              {donnees.priorites.map((t) => (
                <div key={t.id} className="qs-item"><span className="qs-dot" /><div><strong>{t.titre}</strong>
                  <small>{t.echeance ? `Échéance : ${dateHeure(t.echeance)}` : `Priorité ${t.priorite_label.toLowerCase()}`}</small></div></div>
              ))}
              {!donnees.priorites.length && <div className="qc-foot">Aucune tâche urgente aujourd’hui.</div>}
            </div>
            <div className="qc-card" style={{ marginTop: 16 }}>
              <h3>📅 Prochains rendez-vous</h3>
              {donnees.rendez_vous.map((r) => (
                <div key={r.id} className="qs-item"><span className="qs-dot" /><div><strong>{r.titre}</strong>
                  <small>{dateHeure(r.echeance)}{r.ville ? ` · ${r.ville}` : ""}</small></div></div>
              ))}
              {!donnees.rendez_vous.length && <div className="qc-foot">Aucun rendez-vous programmé.</div>}
              <button className="qc-btn qc-btn--sm" style={{ marginTop: 10 }} onClick={() => ouvrirPage("sec_agenda")}>Ouvrir l’agenda</button>
            </div>
            <div className="qc-card" style={{ marginTop: 16 }}>
              <h3>À suivre</h3>
              <button className="qs-suivi" onClick={() => ouvrirPage("sec_documents")}><span>Documents en attente de validation</span><strong>{c.documents_a_valider}</strong></button>
              <button className="qs-suivi" onClick={() => ouvrirPage("sec_courriers")}><span>Courriers à traiter</span><strong>{c.courriers_a_traiter}</strong></button>
              <button className="qs-suivi" onClick={() => ouvrirPage("sec_etablissements")}><span>Établissements à relancer</span><strong>{c.relances}</strong></button>
              <button className="qs-suivi" onClick={() => ouvrirPage("sec_manuels")}><span>Commandes de manuels en cours</span><strong>{c.commandes}</strong></button>
            </div>
          </div>
        </div>
      </>}
    </div>
  );
}

// ── Consultation restreinte : parents, enseignants, élèves ─────────────────────

const ANNUAIRES = {
  parents: {
    titre: "Parents", sousTitre: "Contacts des parents inscrits et suivi de leurs demandes de soutien scolaire.",
    colonnes: ["Référence", "Nom", "Ville", "Téléphone", "Demandes de soutien", "Inscrit le"],
    ligne: (p) => [p.reference, p.nom, p.ville || "—", p.telephone || "—",
      p.demandes ? `${p.demandes}${p.derniere_demande ? ` · dernière : ${p.derniere_demande}` : ""}` : "Aucune", dateHeure(p.inscrit_le)],
  },
  enseignants: {
    titre: "Enseignants", sousTitre: "Retrouvez les enseignants et orientez les demandes de soutien.",
    colonnes: ["Référence", "Nom", "Matières", "Établissement", "Soutien scolaire", "Inscrit le"],
    ligne: (e) => [e.reference, e.nom, e.matieres.join(", ") || "—", [e.etablissement, e.ville].filter(Boolean).join(" · ") || "—",
      e.soutien || (e.plateforme ? "Enseignant de la plateforme" : "—"), dateHeure(e.inscrit_le)],
    note: "La validation des profils enseignants reste au super administrateur : cette liste est en lecture seule.",
  },
  eleves: {
    titre: "Élèves", sousTitre: "Recherchez un élève pour répondre à une demande d’assistance.",
    colonnes: ["Référence", "Nom", "Classe", "École", "Demandes au secrétariat", "Inscrit le"],
    ligne: (e) => [e.reference, e.nom, e.classe || "—", [e.ecole, e.ville].filter(Boolean).join(" · ") || "—", e.demandes || "Aucune", dateHeure(e.inscrit_le)],
  },
};

export function SECAnnuairePage({ categorie, rechercheInitiale = "" }) {
  const cfg = ANNUAIRES[categorie];
  const [donnees, setDonnees] = useState(null);
  const [recherche, setRecherche] = useState(rechercheInitiale);
  const [page, setPage] = useState(1);
  useEffect(() => { setRecherche(rechercheInitiale); }, [rechercheInitiale]);
  useEffect(() => { setPage(1); }, [recherche]);
  useEffect(() => {
    const params = new URLSearchParams({ page: String(page) });
    if (recherche.trim()) params.set("q", recherche.trim());
    const t = setTimeout(() => {
      api.get(`secretariat/annuaire/${categorie}/?${params}`).then((r) => setDonnees(r.data)).catch(() => setDonnees(false));
    }, 250);
    return () => clearTimeout(t);
  }, [categorie, page, recherche]);
  return (
    <div className="qc qs">
      <Entete titre={cfg.titre} sousTitre={cfg.sousTitre} />
      <div className="qs-notice">Consultation restreinte : seules les informations utiles au secrétariat sont affichées (ni e-mail, ni mot de passe,
        ni code, ni paiement). {cfg.note || ""}</div>
      <div className="qc-card">
        <div className="qc-toolbar">
          <input className="qc-search qs-grow" placeholder="Rechercher par nom, ville, école…" value={recherche}
            onChange={(e) => setRecherche(e.target.value)} aria-label="Rechercher" />
        </div>
        {donnees === null ? <Chargement /> : donnees === false ? <div className="qc-empty">Chargement impossible.</div> : (
          <div className="qc-table-wrap">
            <table className="qc-table">
              <thead><tr>{cfg.colonnes.map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {donnees.resultats.map((l) => (
                  <tr key={l.id}>{cfg.ligne(l).map((v, i) => <td key={i}>{i === 0 ? <strong>{v}</strong> : v}</td>)}</tr>
                ))}
                {!donnees.resultats.length && <tr><td colSpan={cfg.colonnes.length} className="qc-empty">
                  {recherche ? "Aucun résultat pour cette recherche." : "Aucune personne inscrite pour le moment."}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {donnees && <Pagination pagination={donnees.pagination} onPage={setPage} />}
      </div>
    </div>
  );
}

// ── Manuels et commandes ────────────────────────────────────────────────────

export function SECManuelsPage() {
  const [manuels] = useManuels();
  return (
    <div className="qc qs">
      <Entete titre="Manuels et commandes" sousTitre="Suivez les demandes et commandes de manuels sans modifier les paiements." />
      <div className="qs-notice">Le secrétariat suit les commandes ; les paiements, les prix et les codes d’activation restent gérés par la direction et la comptabilité.</div>
      <SECRegistrePage type="commande" integre />
      <div className="qc-card" style={{ marginTop: 16 }}>
        <h3>Catalogue des manuels</h3>
        {manuels === null ? <Chargement /> : (
          <div className="qc-table-wrap">
            <table className="qc-table">
              <thead><tr><th>Manuel</th><th>Niveau</th><th>Classes</th><th>Commandes en cours</th><th>Statut</th></tr></thead>
              <tbody>
                {manuels.map((m) => (
                  <tr key={m.id}><td><strong>{m.titre}</strong></td><td>{m.niveau}</td><td>{m.classes.join(", ") || "—"}</td>
                    <td>{m.commandes_en_cours}</td><td><Chip ton={m.publie ? "green" : "grey"}>{m.publie ? "Publié" : "Non publié"}</Chip></td></tr>
                ))}
                {!manuels.length && <tr><td colSpan={5} className="qc-empty">Aucun manuel.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Mon compte ──────────────────────────────────────────────────────────────

export function SECComptePage({ user, onProfil }) {
  return (
    <div className="qc qs">
      <Entete titre="Mon compte" sousTitre="Préférences et informations professionnelles du secrétariat." />
      <div className="qc-cols qc-cols--half">
        <div className="qc-card">
          <h3>Profil professionnel</h3>
          <dl className="qs-details">
            <dt>Nom affiché</dt><dd>{user?.nom_complet || user?.username}</dd>
            <dt>Fonction</dt><dd>Secrétariat</dd>
            <dt>Identifiant</dt><dd>{user?.username}</dd>
          </dl>
          <div className="qc-actions" style={{ justifyContent: "flex-start" }}>
            <button className="qc-btn qc-btn--primary" onClick={onProfil}>Modifier mon profil et mon mot de passe</button>
            <button className="qc-btn" onClick={() => ouvrirPage("ma_signature")}>Ma signature</button>
          </div>
          <div className="qc-foot">Les notifications (nouvelle demande du site, document validé, dossier confié…) arrivent dans la cloche en haut de l’écran.</div>
        </div>
        <div className="qc-card">
          <h3>🔐 Mes droits</h3>
          <ul className="qs-droits">
            <li className="ok">Traiter les demandes du site avec la Community Manager</li>
            <li className="ok">Tenir l’agenda, les tâches, les courriers et les commandes de manuels</li>
            <li className="ok">Préparer des documents et les envoyer à la validation du CEO</li>
            <li className="ok">Rédiger des communiqués et des factures dans la boîte de dépôt</li>
            <li className="ok">Échanger avec toute l’équipe dans la messagerie</li>
            <li className="non">Valider un profil enseignant, rembourser, modifier un tarif ou un paiement</li>
            <li className="non">Voir les mots de passe, les codes manuels ou la comptabilité</li>
            <li className="non">Émettre un document sans validation</li>
          </ul>
          <div className="qc-foot">Les droits d’accès sont définis par l’administration.</div>
        </div>
      </div>
    </div>
  );
}
